
import { supabase } from '@/integrations/supabase/client';
import { Project } from '@/types/project';
import { mapDatabaseProjectToProject } from '@/utils/projectMappers';
import { dataRefresh } from '@/lib/dataRefresh';

// Get site office ID from current logged-in team member or selected site office
const getSiteOfficeId = async (): Promise<string | null> => {
  const selectedSiteOfficeId = localStorage.getItem('selectedSiteOfficeId');
  const selectedSiteOfficeName = localStorage.getItem('selectedSiteOffice');
  const userEmail = localStorage.getItem('userEmail');

  console.log('[createProject] resolve site office', {
    selectedSiteOfficeId,
    selectedSiteOfficeName,
    userEmail,
  });

  // If it's a valid UUID, use it directly
  if (selectedSiteOfficeId && selectedSiteOfficeId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
    console.log('[createProject] using selectedSiteOfficeId UUID:', selectedSiteOfficeId);
    return selectedSiteOfficeId;
  }

  // Otherwise, try to get from logged-in team member
  if (userEmail) {
    const { data: teamMember, error } = await supabase
      .from('team_members')
      .select('site_office_id')
      .eq('email', userEmail)
      .maybeSingle();

    if (error) {
      console.error('[createProject] error reading team_members:', error);
    }

    if (!error && teamMember?.site_office_id) {
      console.log('[createProject] using team member site_office_id:', teamMember.site_office_id);
      return teamMember.site_office_id;
    }
  }

  // Last resort: look up by site office name
  if (selectedSiteOfficeName) {
    const { data: siteOffice, error } = await supabase
      .from('site_offices')
      .select('id')
      .eq('name', selectedSiteOfficeName)
      .maybeSingle();

    if (error) {
      console.error('[createProject] error reading site_offices:', error);
    }

    if (!error && siteOffice?.id) {
      console.log('[createProject] found site office by name:', siteOffice.id);
      localStorage.setItem('selectedSiteOfficeId', siteOffice.id);
      return siteOffice.id;
    }
  }

  console.error('[createProject] could not determine site office ID');
  return null;
};

export const addNewProject = async (project: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>): Promise<Project | null> => {
  try {
    const siteOfficeUuid = await getSiteOfficeId();

    if (!siteOfficeUuid) {
      console.error('No site office selected / could not resolve site office ID');
      throw new Error('Site office belum dipilih atau tidak valid.');
    }

    console.log('Creating project for site office:', siteOfficeUuid);

    // Create the project with site office
    const { data: projectData, error: projectError } = await supabase
      .from('projects')
      .insert([
        {
          title: project.name,
          description: project.description,
          project_number: project.projectNumber,
          pic: project.pic,
          supervisor: project.supervisor || '', // Save supervisor/pengawas field
          status: project.status,
          progress: project.progress || 0,
          start_date: project.startDate,
          end_date: project.endDate,
          location: project.location,
          project_type: project.projectType || 'Project', // Use projectType, not workStatus
          budget_rab: 0,
          site_office_id: siteOfficeUuid,
        },
      ])
      .select()
      .single();

    if (projectError) {
      console.error('Error creating project:', projectError);
      throw projectError;
    }

    console.log('✅ Project created:', projectData);
    console.log('📦 Project input received:', {
      materialsCount: project.materials?.length || 0,
      laborCount: project.labor?.length || 0,
      materials: project.materials,
      labor: project.labor
    });

    // Create materials with schedule dates
    if (project.materials && project.materials.length > 0) {
      const materialsToInsert = project.materials.map((material, index) => ({
        project_id: projectData.id,
        name: material.name,
        quantity: material.quantity || 0,
        unit: material.unit || '',
        unit_price: material.unitPrice || 0,
        value_percentage: material.valuePercentage || 0,
        start_date: material.startDate || null,
        end_date: material.endDate || null,
        description: null,
        sort_order: index + 1,
        remark: material.remark || null,
      }));

      console.log('📦 Inserting materials:', materialsToInsert);

      const { data: materialsData, error: materialsError } = await supabase
        .from('materials')
        .insert(materialsToInsert)
        .select();

      if (materialsError) {
        console.error('❌ Error creating materials:', materialsError);
      } else {
        console.log('✅ Materials created:', materialsData?.length || 0, materialsData);
      }
    } else {
      console.log('⚠️ No materials to insert - project.materials is empty or undefined');
    }

    // Create labor with schedule dates
    if (project.labor && project.labor.length > 0) {
      const laborToInsert = project.labor.map((labor, index) => ({
        project_id: projectData.id,
        name: labor.name,
        quantity: labor.quantity || 0,
        unit: labor.unit || '',
        unit_price: labor.unitPrice || 0,
        value_percentage: labor.valuePercentage || 0,
        start_date: labor.startDate || null,
        end_date: labor.endDate || null,
        description: null,
        sort_order: index + 1,
        remark: labor.remark || null,
      }));

      console.log('👷 Inserting labor:', laborToInsert);

      const { data: laborData, error: laborError } = await supabase
        .from('labors')
        .insert(laborToInsert)
        .select();

      if (laborError) {
        console.error('❌ Error creating labor:', laborError);
      } else {
        console.log('✅ Labor created:', laborData?.length || 0, laborData);
      }
    } else {
      console.log('⚠️ No labor to insert - project.labor is empty or undefined');
    }

    // Fetch the complete project with materials and labor
    const { data: completeProjectData, error: fetchError } = await supabase
      .from('projects')
      .select(`
        *,
        materials (*),
        labors (*)
      `)
      .eq('id', projectData.id)
      .single();

    if (fetchError) {
      console.error('Error fetching complete project:', fetchError);
      return null;
    }

    // Map to local format
    const mappedProject = mapDatabaseProjectToProject({
      id: completeProjectData.id,
      title: completeProjectData.title,
      project_number: completeProjectData.project_number,
      pic: completeProjectData.pic,
      supervisor: (completeProjectData as any).supervisor || '',
      status: completeProjectData.status,
      progress: completeProjectData.progress,
      start_date: completeProjectData.start_date,
      end_date: completeProjectData.end_date,
      created_at: completeProjectData.created_at,
      updated_at: completeProjectData.updated_at,
      description: completeProjectData.description,
      project_type: completeProjectData.project_type,
      location: completeProjectData.location,
      schedule_pdf_url: (completeProjectData as any).schedule_pdf_url || '',
      materials: completeProjectData.materials || [],
      labors: (completeProjectData as any).labors || [],
      metadata: {}
    });

    console.log('✅ Project created and mapped successfully');
    
    // Trigger refresh for projects, materials, and labors
    await dataRefresh.triggerMultiple(['projects', 'materials', 'labors']);

    // Auto-schedule the project in background (fire-and-forget)
    if (project.startDate && project.endDate) {
      supabase.functions.invoke('auto-schedule-project', {
        body: { project_id: projectData.id }
      }).then(({ data, error }) => {
        if (error) console.error('Auto-schedule failed:', error);
        else console.log('✅ Auto-schedule applied:', data);
        // Trigger refresh again after auto-schedule completes
        dataRefresh.triggerMultiple(['materials', 'labors']);
      });
    }
    
    return mappedProject;
  } catch (error) {
    console.error('Error in addNewProject:', error);
    return null;
  }
};
